import { Link } from "react-router-dom";
import { useMemo } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSEO } from "@/hooks/useCanonical";
import { getStaticRouteSeo } from "@/lib/seo";
import { WEDDING_FAQ_ITEMS, buildWeddingJsonLd } from "@/lib/structuredData";

type WeddingImage = {
  url: string;
  caption?: string | null;
  sort_order?: number | null;
};

type WeddingJob = {
  id: string;
  title: string;
  slug: string;
  images?: Array<{
    url: string;
    caption?: string | null;
    sort_order?: number | null;
  }>;
};

const Wedding = () => {
  const faqItems = WEDDING_FAQ_ITEMS;

  const { data: weddingImages = [] } = useQuery<WeddingImage[]>({
    queryKey: ["wedding-images"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("images")
        .select(`
          url,
          caption,
          sort_order,
          jobs!inner(slug)
        `)
        .eq("jobs.slug", "wedding")
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });

  const { data: weddingJobs = [] } = useQuery<WeddingJob[]>({
    queryKey: ["wedding-jobs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("jobs")
        .select(`
          id,
          title,
          slug,
          description,
          categories!inner(slug),
          images(url, caption, sort_order)
        `)
        .eq("categories.slug", "wedding")
        .order("sort_order", { ascending: true });

      if (error) throw error;

      const list = (data || []).filter((job: WeddingJob) => job.images && job.images.length > 0);
      return list.filter((job) => job.slug !== "wedding");
    },
  });

  const galleryImages = useMemo(() => {
    if (weddingImages.length > 0) return weddingImages;

    const flattened = weddingJobs.flatMap((job) =>
      (job.images || []).map((img) => ({ ...img, jobSlug: job.slug }))
    );

    return flattened.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [weddingImages, weddingJobs]);

  const jsonLd = useMemo(
    () => buildWeddingJsonLd({ imageUrls: galleryImages.map((img) => img.url) }),
    [galleryImages],
  );

  const seo = getStaticRouteSeo("wedding", { image: galleryImages[0]?.url });

  useSEO({ ...seo, jsonLd });

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />
      <main className="pt-32 md:pt-40 pb-24 flex-1">
        <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-12 md:mb-16">
          <div className="gap-6 md:gap-10 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-small-heading font-extrabold italic tracking-tight mb-8">
                Bröllopsfotografering
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-foreground/85">
                Dokumentär bröllopsfotograf som fångar allt från tidlösa porträtt till festen som aldrig vill ta slut,
                och alla de små ögonblicken däremellan. Baserad i Stockholm, reser gärna dit ni säger ja.
              </p>
            </div>
          </div>
        </section>

        {galleryImages.length > 0 && (
          <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-12 md:mb-16">
            <div className="overflow-hidden">
              <img
                src={galleryImages[0].url}
                alt={galleryImages[0].caption || "Bröllopsfotograf Stockholm"}
                className="w-full h-auto object-cover"
              />
            </div>
          </section>
        )}

        <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-12 md:mb-16">
          <div className="space-y-6 text-foreground/85 leading-relaxed">
            <p>
              Jag heter Ina och är fotograf med inriktning porträtt, lifestyle och interiör. På er bröllopsdag finns jag
              där för att fånga helheten, från tidlösa porträtt av er två till skratt, tårar, dance moves och alla de
              små ögonblicken däremellan.
            </p>
            <p>
              Min stil är dokumentär. Det innebär att jag fotograferar er dag precis som den är, utan att styra mer än
              nödvändigt. Jag vill berätta er historia så som den verkligen känns med både de storslagna ögonblicken och
              de där fina detaljerna ni kanske inte ens hinner uppfatta själva.
            </p>
            <p>Jag utgår från Stockholm men reser mer än gärna till platsen där ni ska säga JA till varandra.</p>
          </div>
        </section>

        {galleryImages.length > 3 && (
          <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-8 md:mb-12">
            <div className="grid grid-cols-3 gap-3 md:gap-4">
              {galleryImages.slice(1, 4).map((img, index) => (
                <div key={index} className="aspect-[3/4] overflow-hidden">
                  <img
                    src={img.url}
                    alt={img.caption || `Bröllopsfoto ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-12 md:mb-16">
          <div className="bg-secondary/20 p-6 md:p-8 rounded-sm">
            <h2 className="text-xl font-small-heading font-extrabold italic text-foreground mb-4">
              Detta ingår i alla mina paket
            </h2>
            <ul className="list-disc pl-5 space-y-3 text-foreground/80">
              <li>Planeringsmöte med mig.</li>
              <li>Ett personligt webbgalleri där ni kan ladda ner och dela med gäster.</li>
              <li>Minst 50 redigerade högupplösta och webbupplösta bilder per fototimme.</li>
            </ul>
          </div>
        </section>

        <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-12 md:mb-16">
          <div>
            <h2 className="text-xl font-small-heading font-extrabold italic text-foreground mb-6">Paket och priser</h2>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="wiggly-border p-6 flex flex-col gap-3">
                <h3 className="font-small-heading font-extrabold italic text-lg">2 timmar - Sep-Maj</h3>
                <p className="font-normal text-xl">
                  14 000 kr <span className="text-sm text-foreground/70">inkl. moms</span>
                </p>
                <p className="text-sm text-foreground/75">Passar för vigsel och porträtt i Stadshuset.</p>
                <ul className="list-disc pl-5 text-foreground/75 text-xs italic leading-snug space-y-1">
                  <li>Ett personligt webbgalleri där ni kan ladda ner och dela med gäster.</li>
                  <li>100 noggrant utvalda redigerade bilder.</li>
                  <li>Resa inom Stockholm.</li>
                  <li>Planeringsmöte med mig.</li>
                </ul>
                <p className="text-xs text-foreground/60">Endast tillgängligt september–maj.</p>
                <p className="text-xs text-foreground/60">Tillägg (inkl. moms): Analog 35mm filmrulle med framkallade kopior som jag skickar till er. 1 300 kr.</p>
              </div>

              <div className="wiggly-border p-6 flex flex-col gap-3">
                <h3 className="font-small-heading font-extrabold italic text-lg">3 timmar</h3>
                <p className="font-normal text-xl">
                  18 000 kr <span className="text-sm text-foreground/70">inkl. moms</span>
                </p>
                <p className="text-sm text-foreground/75">Perfekt för vigsel, porträtt, början på mingel eller slutet på förberedelser.</p>
                <ul className="list-disc pl-5 text-foreground/75 text-xs italic leading-snug space-y-1">
                  <li>Ett personligt webbgalleri där ni kan ladda ner och dela med gäster.</li>
                  <li>Minst 150 stämningsfulla redigerade bilder.</li>
                  <li>Resa inom Stockholm.</li>
                  <li>Planeringsmöte med mig.</li>
                </ul>
                <p className="text-xs text-foreground/60">Tillägg (inkl. moms): Analog 35mm filmrulle med framkallade kopior som jag skickar till er. 1 300 kr.</p>
              </div>

              <div className="wiggly-border p-6 flex flex-col gap-3">
                <h3 className="font-small-heading font-extrabold italic text-lg">HALVDAG · 5 timmar</h3>
                <p className="font-normal text-xl">
                  25 000 kr <span className="text-sm text-foreground/70">inkl. moms</span>
                </p>
                <p className="text-sm text-foreground/75">Passar till förberedelser, porträtt, vigsel och början av mingel.</p>
                <ul className="list-disc pl-5 text-foreground/75 text-xs italic leading-snug space-y-1">
                  <li>Ett personligt webbgalleri där ni kan ladda ner och dela med gäster.</li>
                  <li>Minst 250 omsorgsfullt redigerade bilder.</li>
                  <li>Resa inom Stockholm.</li>
                  <li>Planeringsmöte med mig.</li>
                  <li>Valfri provfotografering i Stockholm 1–2 månader innan bröllopet (ca 20 min). Efteråt får ni 3 bilder att behålla.</li>
                </ul>
                <p className="text-xs text-foreground/60">Tillägg (inkl. moms): Analog 35mm filmrulle med framkallade kopior, 1 300 kr.</p>
              </div>

            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-10">
              <div className="wiggly-border p-6 flex flex-col gap-3">
                <h3 className="font-small-heading font-extrabold italic text-lg">DAG · 8 timmar</h3>
                <p className="font-normal text-xl">
                  34 000 kr <span className="text-sm text-foreground/70">inkl. moms</span>
                </p>
                <p className="text-sm text-foreground/75">
                  För er som vill ha större delen av dagen dokumenterad, från förberedelser eller porträtt till vigsel och början av middagen.
                </p>
                <ul className="list-disc pl-5 text-foreground/75 text-xs italic leading-snug space-y-1">
                  <li>Ett personligt webbgalleri där ni kan ladda ner och dela med gäster.</li>
                  <li>Minst 400 bilder, noggrant utvalda och redigerade.</li>
                  <li>Resa inom Stockholm.</li>
                  <li>Planeringsmöte med mig.</li>
                  <li>Valfri provfotografering i Stockholm 1–2 månader innan bröllopet (ca 20 min). Efteråt får ni 3 bilder att behålla.</li>
                </ul>
                <p className="text-xs text-foreground/60">Tillägg (inkl. moms): Analog 35mm filmrulle med framkallade kopior som jag skickar till er. 1 300 kr.</p>
              </div>

              <div className="wiggly-border p-6 flex flex-col gap-3">
                <h3 className="font-small-heading font-extrabold italic text-lg">HELDAG · 10 timmar</h3>
                <p className="font-normal text-xl">
                  42 000 kr <span className="text-sm text-foreground/70">inkl. moms</span>
                </p>
                <p className="text-sm text-foreground/75">En hel dags dokumentation från de första förberedelserna till sista festen.</p>
                <ul className="list-disc pl-5 text-foreground/75 text-xs italic leading-snug space-y-1">
                  <li>Ett personligt webbgalleri där ni kan ladda ner och dela med gäster.</li>
                  <li>Minst 500 bilder, handplockade och redigerade med omsorg.</li>
                  <li>Resa och boende i hela Sverige ingår.</li>
                  <li>Planeringsmöte med mig.</li>
                  <li>Valfri provfotografering i Stockholm 1–2 månader innan bröllopet (ca 20 min). Efteråt får ni 3 bilder att behålla.</li>
                  <li>En analog 35mm filmrulle med framkallade kopior som jag skickar till er ingår.</li>
                </ul>
                <p className="text-xs text-foreground/60">Vill ni ha fler filmrullar kostar det 1 300 kr per rulle.</p>
              </div>
            </div>

            <div className="bg-secondary/20 p-6 rounded-sm mb-10 text-left leading-relaxed space-y-4">
              <div className="space-y-3">
                <h3 className="font-small-heading font-extrabold italic text-lg">Hur bokar vi dig?</h3>
                <p className="text-sm text-foreground/75">
                  Skicka en bokningsförfrågan genom att fylla i formuläret längst ner på denna sida. Jag återkommer inom
                  ett par dagar. Det går också bra att maila direkt till ina.nederdal@gmail.com.
                </p>
                <p className="text-sm text-foreground/75">
                  Om jag är ledig ert datum så bokar vi in ett videomöte eller träff (valfritt såklart!). Om allt känns
                  bra skickar jag ett digitalt kontrakt och sedan en bokningsfaktura på 30% av summan av ert valda paket.
                  När den är betald är ert datum bokat!
                </p>
              </div>

              <div className="space-y-3">
                <h3 className="font-small-heading font-extrabold italic text-lg">Redigering & leverans</h3>
                <p className="text-sm text-foreground/75">Jag redigerar vissa bilder i färg och andra i svartvitt.</p>
                <p className="text-sm text-foreground/75">
                  En till tre dagar efter bröllopet skickar jag 1-3 sneak peek-bilder. Efter 4-8 veckor när resten av
                  bilderna är färdiga laddas de upp i ert privata webbgalleri. Fakturan med resterande belopp kommer på
                  mailen i samband med leveransen. När fakturan är betald, skickar jag även de högupplösta bilderna till
                  er!
                </p>
              </div>

              <div className="space-y-3">
                <h3 className="font-small-heading font-extrabold italic text-lg">Fler timmar?</h3>
                <p className="text-sm text-foreground/75">
                  Vill ni ha fler timmar finns möjlighet att lägga till även efter bokning.
                </p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-lg text-foreground/85 mb-8">
                Vill ni boka bröllopsfoto eller har frågor? Hör av er så planerar vi dagen tillsammans.
              </p>
              <Button
                asChild
                size="lg"
                className="bg-red-500 text-white hover:bg-red-600 px-8 py-6 text-base font-medium"
              >
                <Link to="/booking">Skicka förfrågan</Link>
              </Button>
            </div>

          </div>
        </section>

        {galleryImages.length > 4 && (
          <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-12 md:mb-16">
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {galleryImages.slice(4, 8).map((img, index) => (
                <div key={index} className="aspect-[3/4] overflow-hidden">
                  <img
                    src={img.url}
                    alt={img.caption || `Bröllopsfoto ${index + 5}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-12 md:mb-16">
          <h2 className="text-xl font-small-heading font-extrabold italic text-foreground mb-8">Vanliga frågor</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index + 1}`} className="border-b border-foreground/10">
                <AccordionTrigger className="text-left text-base hover:no-underline py-5">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-foreground/70 leading-relaxed pb-5">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {weddingJobs.length > 0 && (
          <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-12 md:mb-16">
            <h2 className="text-xl font-small-heading font-extrabold italic text-foreground mb-8">Se fler bröllop</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-10">
              {weddingJobs.slice(0, 6).map((job) => {
                const firstImage = job.images?.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))[0];
                return (
                  <Link key={job.id} to={`/work/wedding/${job.slug}`} className="group">
                    <div className="aspect-[3/4] overflow-hidden mb-3">
                      {firstImage && (
                        <img src={firstImage.url} alt={job.title} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <p className="text-sm text-foreground/100 group-hover:text-foreground transition-colors inline-block border-b border-transparent group-hover:border-red-500">
                      {job.title}
                    </p>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Wedding;
